import { useEffect, useState, useCallback } from "react";
import { usePlaidLink } from "react-plaid-link";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Landmark, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// PlaidLinkButton — drop-in "Connect bank" button for buyers.
// 1. Mints a link_token from our backend (server-side, not exposed in JS bundle)
// 2. Hands it to Plaid Link, which renders the bank-picker / OAuth flow
// 3. On success, exchanges public_token via backend → access_token persisted
//    server-side, accounts mirrored into payment_methods.
// 4. Calls onConnected so the parent can refresh.
//
// Props:
//   onConnected(result)  — fires on success with backend exchange response
//   variant              — "primary" | "secondary" (default primary)
//   label                — button text (default "Connect bank account")
export default function PlaidLinkButton({ onConnected, variant = "primary", label = "Connect bank account" }) {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exchanging, setExchanging] = useState(false);

  // Fetch a fresh link token whenever the button mounts.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.post("/onboarding/plaid/link-token", {})
      .then(r => { if (!cancelled) setLinkToken(r.data.link_token); })
      .catch(e => {
        if (cancelled) return;
        const msg = e.response?.data?.detail || "Couldn't start Plaid";
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const onSuccess = useCallback(async (public_token, metadata) => {
    setExchanging(true);
    try {
      const { data } = await api.post("/onboarding/plaid/exchange", {
        public_token,
        institution: metadata?.institution || null,
      });
      toast.success(`✓ ${metadata?.institution?.name || "Bank"} connected · ${data.ach_capable} account${data.ach_capable === 1 ? "" : "s"} ready for ACH`);
      onConnected?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't link bank");
    } finally {
      setExchanging(false);
    }
  }, [onConnected]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: (err) => {
      if (err) toast.error(err.display_message || err.error_message || "Plaid Link exited");
    },
  });

  const disabled = !ready || !linkToken || loading || exchanging;
  const cls = variant === "primary"
    ? "rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4"
    : "rounded-full h-10 px-4";

  return (
    <Button
      onClick={() => open()}
      disabled={disabled}
      data-testid="plaid-link-btn"
      className={cls}
    >
      {exchanging
        ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Linking…</>
        : loading
        ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Preparing…</>
        : <><Landmark className="w-4 h-4 mr-1.5"/>{label}</>}
    </Button>
  );
}

// PlaidConnectionCard — visual "your bank is connected" state with cashflow snapshot.
// Use on Buyer Payments → Methods or Onboarding wizard.
export function PlaidConnectionCard({ summary, onRefresh, refreshing }) {
  if (!summary?.connected) return null;
  const item = summary.item;
  const cf = item?.cashflow || {};
  return (
    <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5" data-testid="plaid-connection-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white grid place-items-center shrink-0">
          <CheckCircle2 className="w-5 h-5 text-emerald-700"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="display text-base">{item.institution_name || "Bank"} connected</div>
            <span className="chip text-[10px] mono uppercase tracking-widest border bg-white">
              via Plaid · {item.env}
            </span>
          </div>
          <div className="text-xs text-emerald-900/70 mt-0.5">
            {(item.accounts || []).length} account{(item.accounts || []).length === 1 ? "" : "s"} · {(item.accounts || []).filter(a => a.ach_routing).length} ACH-ready
          </div>
        </div>
        {onRefresh && (
          <Button onClick={onRefresh} disabled={refreshing} variant="outline" size="sm" data-testid="plaid-refresh-btn"
            className="rounded-full text-xs h-8">
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin"/> : "Refresh"}
          </Button>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <CashflowTile l="Avg monthly revenue" v={cf.avg_monthly_revenue ? `$${Number(cf.avg_monthly_revenue).toLocaleString()}` : "—"}/>
        <CashflowTile l="Total inflow"   v={cf.total_revenue ? `$${Number(cf.total_revenue).toLocaleString()}` : "—"}/>
        <CashflowTile l="Transactions"   v={String(cf.transactions_count || 0)}/>
        <CashflowTile l="NSF events"     v={String(cf.nsf_count || 0)} warn={cf.nsf_count > 0}/>
      </div>
    </div>
  );
}

function CashflowTile({ l, v, warn }) {
  return (
    <div className={`rounded-xl bg-white/80 border px-3 py-2.5 ${warn ? "border-amber-300" : "border-white/60"}`}>
      <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{l}</div>
      <div className={`display text-base mt-1 ${warn ? "text-amber-700" : ""}`}>{v}</div>
    </div>
  );
}
